<script setup lang="ts">
import type { SettingsResponse } from "@maimu/contracts";
import { computed, onMounted, ref } from "vue";

import { api } from "@/lib/api.js";

/**
 * The model half of the admin panel (D18).
 *
 * Free text rather than a dropdown, on purpose: a list of model names goes stale the
 * week OpenAI ships the next one, and a panel that cannot express the model you want
 * to use sends someone back to editing the container's environment. The server
 * validates the shape and warns about capabilities it does not recognise — it does
 * not pretend to know every model that exists.
 */
const state = ref<SettingsResponse | null>(null);
const model = ref("");
const visionModel = ref("");
const loading = ref(true);
const saving = ref(false);
const error = ref<string | null>(null);
const saved = ref(false);

const dirty = computed(
  () =>
    !!state.value &&
    (model.value !== state.value.settings.model || visionModel.value !== state.value.settings.visionModel),
);

function apply(next: SettingsResponse): void {
  state.value = next;
  model.value = next.settings.model;
  visionModel.value = next.settings.visionModel;
}

async function run(action: () => Promise<SettingsResponse>): Promise<void> {
  saving.value = true;
  error.value = null;
  saved.value = false;
  try {
    apply(await action());
    saved.value = true;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "Seadete salvestamine ebaõnnestus.";
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  try {
    apply(await api.adminSettings());
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "Seadeid ei õnnestunud laadida.";
  } finally {
    loading.value = false;
  }
});

const when = (iso: string): string =>
  new Date(iso).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
</script>

<template>
  <section class="settings">
    <p v-if="loading" class="hint">Laen…</p>

    <template v-else-if="state">
      <p class="hint">
        Muudatus kehtib järgmisest küsimusest — konteinerit taaskäivitama ei pea. Temperatuur, arutlusaste ja
        märgilimiit jäävad keskkonnamuutujatesse; embedding-mudel samuti, sest selle vahetus nõuab kogu teadmusbaasi
        uuesti indekseerimist.
      </p>

      <label class="field">
        <span>Vastusemudel</span>
        <input v-model.trim="model" type="text" spellcheck="false" :disabled="saving" />
        <small>
          Vaikeväärtus keskkonnast: <code>{{ state.defaults.model || "—" }}</code>
          <button
            v-if="state.overridden.includes('model')"
            class="linkish"
            type="button"
            :disabled="saving"
            @click="run(() => api.resetAdminSetting('model'))"
          >
            taasta vaikeväärtus
          </button>
        </small>
      </label>

      <label class="field">
        <span>Pildimudel</span>
        <input
          v-model.trim="visionModel"
          type="text"
          spellcheck="false"
          placeholder="sama mis vastusemudel"
          :disabled="saving"
        />
        <small>
          Loeb ekraanipilte. Tühi tähendab, et kasutatakse vastusemudelit.
          <button
            v-if="state.overridden.includes('visionModel')"
            class="linkish"
            type="button"
            :disabled="saving"
            @click="run(() => api.resetAdminSetting('visionModel'))"
          >
            taasta vaikeväärtus
          </button>
        </small>
      </label>

      <p v-for="warning in state.warnings" :key="warning" class="warn">{{ warning }}</p>
      <p v-if="error" class="error">{{ error }}</p>
      <p v-else-if="saved && !dirty" class="ok">Salvestatud.</p>

      <div class="actions">
        <button
          class="btn"
          type="button"
          :disabled="saving || !dirty"
          @click="run(() => api.saveAdminSettings({ model, visionModel }))"
        >
          Salvesta
        </button>
        <span v-if="state.updatedAt" class="hint small">
          Viimati muutis {{ state.updatedBy ?? "keegi" }} {{ when(state.updatedAt) }}
        </span>
      </div>
    </template>
  </section>
</template>

<style scoped>
.settings {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  max-width: 520px;
}

.field > span {
  font-size: 0.82rem;
  font-weight: 600;
}

input {
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 0.5rem 0.6rem;
  font: inherit;
  font-size: 0.9rem;
  background: #fff;
  color: var(--ink);
}

small {
  color: var(--muted);
  font-size: 0.76rem;
}

code {
  background: var(--soft);
  padding: 0.05rem 0.3rem;
  border-radius: 4px;
}

.linkish {
  border: 0;
  background: none;
  padding: 0 0 0 0.4rem;
  font: inherit;
  font-size: 0.76rem;
  color: var(--green);
  cursor: pointer;
  text-decoration: underline;
}

.hint {
  color: var(--muted);
  font-size: 0.82rem;
  margin: 0;
  max-width: 640px;
}

.hint.small {
  font-size: 0.76rem;
}

.warn,
.error,
.ok {
  margin: 0;
  font-size: 0.82rem;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 0.5rem 0.7rem;
  max-width: 640px;
}

.warn {
  color: var(--gold-dark);
  border-color: var(--gold);
}

.error {
  color: var(--warn);
  border-color: var(--warn);
}

.ok {
  color: var(--green);
  border-color: var(--green);
}

.actions {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;
}
</style>
